import { IconifyIcon } from '@iconify/react';
import { useCallback } from 'react';
import { Whisper } from 'rsuite';
import {
  NavItemActiveIndicator,
  NavItemSectionWrapper,
  NavItemWrapper,
  StyledIcon,
  StyledIconWrapper,
  StyledTooltip,
} from './nav-item-styles';

type NavItemProps = {
  /** Unique identifier for the navigation item */
  id: string;

  /** Icon to display; can be a string or an IconifyIcon object */
  icon: string | IconifyIcon;

  /** Tooltip text shown on hover */
  tooltip: string;

  /** Indicates if the nav item is currently active */
  isActive: boolean;

  /** Function to call when the item is clicked */
  onClick: () => void;

  /** Aria label for accessibility. Should describe the function of the element, not just the icon. */
  ariaLabel: string;

  /** Disables the nav item if true (default is false) */
  disabled?: boolean;

  /** Hides the nav item if true */
  hide?: boolean;
};

export function NavItem(props: NavItemProps) {
  const {
    id,
    icon,
    isActive,
    onClick,
    tooltip,
    ariaLabel,
    hide,
    disabled = false,
  } = props;

  const navItemClickCB = useCallback(() => {
    if (disabled) return;
    onClick();
  }, [disabled, onClick]);

  if (hide) return null;

  const elementIdentifier = `nav-item-${id}`;
  const tooltipIdentifier = `vertical-nav-tooltip-${id}`;

  return (
    <NavItemSectionWrapper
      aria-describedby={tooltipIdentifier} // Links to the tooltip
      aria-label={ariaLabel} // Describes the function
      data-cy={elementIdentifier}
      data-testid={elementIdentifier}
      onClick={navItemClickCB}
    >
      <NavItemActiveIndicator isActive={isActive} />

      <Whisper
        trigger="hover"
        placement="right"
        speaker={
          <StyledTooltip id={tooltipIdentifier}>{tooltip}</StyledTooltip>
        }
      >
        <NavItemWrapper>
          <StyledIconWrapper>
            <StyledIcon disabled={disabled} isActive={isActive} icon={icon} />
          </StyledIconWrapper>
        </NavItemWrapper>
      </Whisper>
    </NavItemSectionWrapper>
  );
}
